import { useCallback } from "react";
import { useAntdMessage } from "./useAntdMessage";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  agentsSelector,
  isActiveISOSettingSelector, // [crm-iso]
  userSelector,
} from "appredux/selectors";

const useTicketingEventHandler = () => {
  const { t } = useTranslation();
  const { notification } = useAntdMessage();
  const queryClient = useQueryClient();
  const agents = useSelector(agentsSelector);
  const user = useSelector(userSelector);
  const isActiveISO = useSelector(isActiveISOSettingSelector); // [crm-iso]

  // update sider bubble count
  const updateInboxTicketing = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: ["inboxTicketing", { active_only: 1, include_count: 1 }],
    });
  }, [queryClient]);

  // update dengan tickets key yang aktif saja, jika tidak ada observer maka tidak perlu diinvalidate
  const refetchActiveTicketsQueries = useCallback(() => {
    const queryCache = queryClient.getQueryCache();
    const allQueries = queryCache.findAll({
      queryKey: ["tickets"],
      exact: false,
    });

    // Filter hanya query yang memiliki observer aktif
    const activeQueries = allQueries.filter((query) => {
      return query.getObserversCount() > 0;
    });

    // Invalidate hanya yang aktif
    activeQueries.forEach((query) => {
      queryClient.invalidateQueries({
        queryKey: query.queryKey,
      });
    });
  }, [queryClient]);

  // update detail ticket jika user sedang mengedit detail ticket
  const invalidateActiveTicketDetail = useCallback((ticketId) => {
    if (!ticketId) return;

    const queryCache = queryClient.getQueryCache();
    const ticketDetailQuery = queryCache.find({
      queryKey: ["ticketById", ticketId.toString()],
      exact: true,
    });

    // Hanya invalidate jika query ada dan memiliki observer aktif
    if (ticketDetailQuery && ticketDetailQuery.getObserversCount() > 0) {
      queryClient.invalidateQueries({
        queryKey: ["ticketById", ticketId.toString()],
        exact: true,
      });
    }
  }, [queryClient]);

  const ticketingHandler = useCallback(
    async (payload) => {
      // [crm-iso] Agen terpengaruh tidak menerima event/notifikasi tiket apa pun.
      // TEARDOWN: hapus baris di bawah + isActiveISO dari deps.
      if (isActiveISO) return;
      const { data_type, created_by } = payload;
      const getAgentName = agents.find((agent) => agent.id === created_by)?.name || "Agent";
      switch (data_type) {

        case "ticket_assigned":
          notification.info({
            message: t("ticketing.notification.ticket_assigned"),
            description: t("ticketing.notification.ticket_assigned_desc", {
              value: payload.title,
            }),
          });
          refetchActiveTicketsQueries();
          // kalau misal user yang saat ini adalah yang update, dia ga perlu invalidate, karena sudah manual refetch setelah update
          if (user.id !== created_by) {
            invalidateActiveTicketDetail(payload.ticket_id);
          }
          break;



        case "ticket_inbox_changed":
          notification.success({
            message: t("ticketing.notification.ticket_inbox_changed"),
            description: t("ticketing.notification.ticket_inbox_changed_desc", {
              value: payload.title,
              user: getAgentName,
            }),
          });
          updateInboxTicketing()
          refetchActiveTicketsQueries();
          // kalau misal user yang saat ini adalah yang update, dia ga perlu invalidate, karena sudah manual refetch setelah update
          if (user.id !== created_by) {
            invalidateActiveTicketDetail(payload.ticket_id)
          }
          break;

        case "ticket_created":
          notification.success({
            message: t("ticketing.notification.new_ticket"),
            description: t("ticketing.notification.new_ticket_desc", {
              value: payload.title,
            }),
          });
          updateInboxTicketing();
          refetchActiveTicketsQueries();
          break;


        case "ticket_deleted":
          notification.warning({
            message: t("ticketing.notification.ticket_deleted"),
            description: t("ticketing.notification.ticket_deleted_desc", {
              value: payload.title,
            }),
          });
          updateInboxTicketing();
          refetchActiveTicketsQueries();
          // kalau misal user yang saat ini adalah yang update, dia ga perlu invalidate, karena sudah manual refetch setelah update
          if (user.id !== created_by) {
            invalidateActiveTicketDetail(payload.ticket_id)
          }
          break;

        case "ticket_comment_added":
          notification.info({
            message: t("ticketing.notification.new_comment"),
            description: t("ticketing.notification.new_comment_desc", {
              value: payload.title,
            }),
          });
          // kalau misal user yang saat ini adalah yang update, dia ga perlu invalidate, karena sudah manual refetch setelah update
          if (user.id !== created_by) {
            invalidateActiveTicketDetail(payload.ticket_id)
          }
          break;

        case "ticket_comment_deleted":
          notification.info({
            message: t("ticketing.notification.comment_deleted"),
            description: t("ticketing.notification.comment_deleted_desc", {
              value: payload.title,
            }),
          });
          // kalau misal user yang saat ini adalah yang update, dia ga perlu invalidate, karena sudah manual refetch setelah update
          if (user.id !== created_by) {
            invalidateActiveTicketDetail(payload.ticket_id)
          }
          break;

        default:
          console.log("Unknown ticketing event:", data_type);
          break;
      }
    },
    [t, notification, updateInboxTicketing, refetchActiveTicketsQueries, user, agents, invalidateActiveTicketDetail, isActiveISO] // [crm-iso] deps: isActiveISO
  );

  return { ticketingHandler };
};

export default useTicketingEventHandler;
